import type { BroadcastCredentials } from "./api";

export interface MediaDeviceOption {
    readonly deviceId: string;
    readonly label: string;
}

export interface PreparedBroadcastStudio {
    readonly videoDevices: MediaDeviceOption[];
    readonly audioDevices: MediaDeviceOption[];
    readonly selectedVideoDeviceId: string | null;
    readonly selectedAudioDeviceId: string | null;
}

interface BroadcastClientLike {
    addAudioInputDevice(device: MediaStream, name: string): Promise<void>;
    addVideoInputDevice(
        device: MediaStream,
        name: string,
        position: { index: number; x?: number; y?: number; width?: number; height?: number; },
    ): Promise<void>;
    attachPreview(element: HTMLCanvasElement): void;
    detachPreview(): void;
    delete(): void;
    getCanvasDimensions(): { width: number; height: number; };
    on(event: string, callback: (payload: unknown) => void): void;
    off(event: string, callback: (payload: unknown) => void): void;
    startBroadcast(streamKey: string, ingestEndpoint?: string): Promise<void | Error>;
    stopBroadcast(): void;
}

type BroadcastSdkModule = typeof import("amazon-ivs-web-broadcast");

const videoDeviceName = "camera";
const audioDeviceName = "microphone";
const connectionStateChangeEvent = "connectionStateChange";
const activeStateChangeEvent = "activeStateChange";

export function isWebBroadcastSupported(): boolean {
    return typeof window !== "undefined"
        && typeof navigator !== "undefined"
        && Boolean(navigator.mediaDevices?.getUserMedia)
        && Boolean(navigator.mediaDevices?.enumerateDevices);
}

export class WebBroadcastController {
    private client: BroadcastClientLike | null = null;
    private previewCanvas: HTMLCanvasElement | null = null;
    private videoStream: MediaStream | null = null;
    private audioStream: MediaStream | null = null;
    private connectionStateListener: ((payload: unknown) => void) | null = null;
    private activeStateListener: ((payload: unknown) => void) | null = null;
    private onConnectionStateChange: ((connectionState: string | null) => void) | null = null;

    public attachPreviewCanvas(canvas: HTMLCanvasElement | null): void {
        this.previewCanvas = canvas;

        if (!canvas || !this.client) {
            return;
        }

        this.client.detachPreview();
        this.client.attachPreview(canvas);
    }

    public async prepare(
        credentials: Pick<BroadcastCredentials, "ingestEndpoint">,
        options: {
            videoDeviceId?: string | null;
            audioDeviceId?: string | null;
            onConnectionStateChange?: ((connectionState: string | null) => void) | null;
            onActiveStateChange?: ((active: boolean) => void) | null;
        } = {},
    ): Promise<PreparedBroadcastStudio> {
        if (!isWebBroadcastSupported()) {
            throw new Error("Browser broadcasting is not supported in this browser");
        }

        const sdk = await loadBroadcastSdk();
        const client = sdk.create({
            ingestEndpoint: credentials.ingestEndpoint,
            streamConfig: sdk.STANDARD_LANDSCAPE,
        }) as unknown as BroadcastClientLike;

        const videoStream = await navigator.mediaDevices.getUserMedia({
            video: {
                deviceId: options.videoDeviceId ? { exact: options.videoDeviceId } : undefined,
                width: { ideal: 1280 },
                height: { ideal: 720 },
                frameRate: { ideal: 30, max: 30 },
            },
            audio: false,
        });

        const audioStream = await navigator.mediaDevices.getUserMedia({
            audio: {
                deviceId: options.audioDeviceId ? { exact: options.audioDeviceId } : undefined,
                echoCancellation: true,
                noiseSuppression: true,
            },
            video: false,
        });
        const canvasDimensions = client.getCanvasDimensions();

        await client.addVideoInputDevice(videoStream, videoDeviceName, {
            index: 0,
            x: 0,
            y: 0,
            width: canvasDimensions.width,
            height: canvasDimensions.height,
        });
        await client.addAudioInputDevice(audioStream, audioDeviceName);

        if (this.previewCanvas) {
            client.attachPreview(this.previewCanvas);
        }

        const connectionStateListener = (payload: unknown) => {
            options.onConnectionStateChange?.(typeof payload === "string" ? payload : null);
        };
        const activeStateListener = (payload: unknown) => {
            if (typeof payload === "boolean") {
                options.onActiveStateChange?.(payload);
            }
        };
        client.on(connectionStateChangeEvent, connectionStateListener);
        client.on(activeStateChangeEvent, activeStateListener);

        this.disposeClient();
        this.client = client;
        this.videoStream = videoStream;
        this.audioStream = audioStream;
        this.connectionStateListener = connectionStateListener;
        this.activeStateListener = activeStateListener;
        this.onConnectionStateChange = options.onConnectionStateChange ?? null;

        const devices = await navigator.mediaDevices.enumerateDevices();
        return {
            videoDevices: devices
                .filter((device) => device.kind === "videoinput")
                .map((device, index) => ({
                    deviceId: device.deviceId,
                    label: device.label || `Camera ${index + 1}`,
                })),
            audioDevices: devices
                .filter((device) => device.kind === "audioinput")
                .map((device, index) => ({
                    deviceId: device.deviceId,
                    label: device.label || `Microphone ${index + 1}`,
                })),
            selectedVideoDeviceId: videoStream.getVideoTracks()[0]?.getSettings().deviceId ?? options.videoDeviceId ?? null,
            selectedAudioDeviceId: audioStream.getAudioTracks()[0]?.getSettings().deviceId ?? options.audioDeviceId ?? null,
        };
    }

    public async start(credentials: BroadcastCredentials): Promise<void> {
        if (!this.client) {
            throw new Error("Prepare the browser studio before starting the broadcast");
        }

        const result = await this.client.startBroadcast(credentials.streamKeyValue, credentials.ingestEndpoint);
        if (result instanceof Error) {
            throw result;
        }
    }

    public stop(): void {
        this.client?.stopBroadcast();
        this.onConnectionStateChange?.("disconnected");
    }

    public dispose(): void {
        this.disposeClient();
        this.previewCanvas = null;
        this.onConnectionStateChange = null;
    }

    private disposeClient(): void {
        if (this.client) {
            if (this.connectionStateListener) {
                this.client.off(connectionStateChangeEvent, this.connectionStateListener);
            }
            if (this.activeStateListener) {
                this.client.off(activeStateChangeEvent, this.activeStateListener);
            }

            this.client.detachPreview();
            this.client.delete();
            this.client = null;
        }

        stopMediaStream(this.videoStream);
        stopMediaStream(this.audioStream);
        this.videoStream = null;
        this.audioStream = null;
        this.connectionStateListener = null;
        this.activeStateListener = null;
    }
}

async function loadBroadcastSdk(): Promise<BroadcastSdkModule> {
    return import("amazon-ivs-web-broadcast");
}

function stopMediaStream(stream: MediaStream | null): void {
    stream?.getTracks().forEach((track) => track.stop());
}
